/**
 * salesPeriod.ts — the client half of the ONE period model quotas and the
 * Forecast grid share (internal/modules/sales/period.go, 2.3b F-4/F-6).
 *
 * THE RULE, restated from the server: a period is a calendar month or a
 * calendar quarter in UTC, named by its FIRST DAY, and its window is
 * HALF-OPEN [start, end). The server REFUSES a periodStart that is not a
 * first day (a named 400 that says which date to send) rather than
 * truncating it — so this module's job is to never produce one. Both the
 * Quotas card and the Forecast tab derive {periodKind, periodStart} here and
 * send BOTH explicitly; the label a reader sees and the window the server
 * computed are then the same arithmetic on the same string.
 *
 * Quarters are calendar quarters (Jan / Apr / Jul / Oct). Everything is UTC
 * getters and Date.UTC — a local-time getter would name the wrong month for
 * a reader west of Greenwich on the evening of the 30th.
 */
import type { SalesPeriodKind } from '../../../../api/types'

export interface SalesPeriod {
  kind: SalesPeriodKind
  /** ISO date (YYYY-MM-DD), always the period's first day. */
  start: string
}

const ISO = (d: Date) => d.toISOString().slice(0, 10)

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The first day of the month/quarter containing `at`, as an ISO date. */
export function periodStartOf(at: Date, kind: SalesPeriodKind): string {
  const y = at.getUTCFullYear()
  const m = at.getUTCMonth() // 0-11
  const first = kind === 'quarter' ? Math.floor(m / 3) * 3 : m
  return ISO(new Date(Date.UTC(y, first, 1)))
}

/** The period containing `now` — the server's own default when both query
 *  fields are omitted, so an unparameterised read and this agree. */
export function currentPeriod(kind: SalesPeriodKind, now: Date = new Date()): SalesPeriod {
  return { kind, start: periodStartOf(now, kind) }
}

function startDate(p: SalesPeriod): Date {
  const [y, m, d] = p.start.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d))
}

/** The EXCLUSIVE end: the next period's first day (period.go periodEndOf). */
export function periodEndExclusive(p: SalesPeriod): string {
  const s = startDate(p)
  const months = p.kind === 'quarter' ? 3 : 1
  return ISO(new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + months, 1)))
}

/** The INCLUSIVE end: the period's last calendar day — the exclusive end
 *  minus one day (§33 W-3). This is what `closed_to` on GET /v1/sales/deals
 *  takes: the server treats that bound as a whole DAY (`closed_at <
 *  closed_to + 1 day`, module.go ListDeals), so a drill that sent the
 *  EXCLUSIVE end would admit every deal closed on the next period's first
 *  day. Both values select the same rows on a tenant with no boundary-day
 *  close — which is why this is pinned on the request, not on a live proof. */
export function periodEndInclusive(p: SalesPeriod): string {
  const endExcl = startDate({ kind: p.kind, start: periodEndExclusive(p) })
  return ISO(new Date(endExcl.getTime() - 86_400_000))
}

/** The adjacent period, forwards (+1) or back (−1). */
export function shiftPeriod(p: SalesPeriod, delta: 1 | -1): SalesPeriod {
  const s = startDate(p)
  const months = (p.kind === 'quarter' ? 3 : 1) * delta
  return { kind: p.kind, start: ISO(new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth() + months, 1))) }
}

/** The same moment under the other kind: the quarter containing this month,
 *  or the first month of this quarter. Re-normalised, so it is always a
 *  first day and never a refusal. */
export function switchPeriodKind(p: SalesPeriod, kind: SalesPeriodKind): SalesPeriod {
  if (kind === p.kind) return p
  return { kind, start: periodStartOf(startDate(p), kind) }
}

/** "Q3 2026" / "Sep 2026" — the name a manager uses for the window. */
export function periodLabel(p: SalesPeriod): string {
  const s = startDate(p)
  const y = s.getUTCFullYear()
  if (p.kind === 'quarter') return `Q${Math.floor(s.getUTCMonth() / 3) + 1} ${y}`
  return `${MONTH_ABBR[s.getUTCMonth()]} ${y}`
}

/** "1 Jul – 30 Sep 2026": the INCLUSIVE calendar span the half-open window
 *  covers, for a reader who does not think in exclusive bounds. */
export function periodRangeLabel(p: SalesPeriod): string {
  const s = startDate(p)
  const last = startDate({ kind: p.kind, start: periodEndInclusive(p) })
  const day = (d: Date) => `${d.getUTCDate()} ${MONTH_ABBR[d.getUTCMonth()]}`
  if (s.getUTCFullYear() === last.getUTCFullYear()) return `${day(s)} – ${day(last)} ${last.getUTCFullYear()}`
  return `${day(s)} ${s.getUTCFullYear()} – ${day(last)} ${last.getUTCFullYear()}`
}
